import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, getToken, setToken, clearToken, ApiError } from "../api/client";

export interface Operator {
  email: string;
  name?: string;
  picture?: string;
}

interface AuthState {
  user: Operator | null;
  loading: boolean;
  /** Exchange a Google ID token (or, in dev-auth, an email) for a session. */
  loginWithIdToken: (idToken: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount: (1) capture a session token handed back by the OAuth redirect
  // callback via the URL fragment (#token=…), then (2) validate the stored token
  // against /api/auth/me.
  useEffect(() => {
    let cancelled = false;
    if (window.location.hash.startsWith("#token=")) {
      const handed = decodeURIComponent(
        window.location.hash.slice("#token=".length),
      );
      if (handed) setToken(handed);
      // Strip the fragment so the JWT doesn't linger in the URL or history.
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
    (async () => {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const { user } = await api<{ user: Operator }>("/api/auth/me");
        if (!cancelled) setUser(user);
      } catch {
        clearToken();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithIdToken = useCallback(async (idToken: string) => {
    const { token, user } = await api<{ token: string; user: Operator }>(
      "/api/auth/google",
      { method: "POST", body: JSON.stringify({ idToken }) },
    );
    setToken(token);
    setUser(user);
  }, []);

  const logout = useCallback(() => {
    void api("/api/auth/logout", { method: "POST" }).catch(() => {});
    clearToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginWithIdToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { ApiError };
